/**
 * Precompilazione del modulo "Nuovo Lead" aperto da una chat (WhatsApp, Instagram,
 * Messenger). Modulo puro, testato in __tests__/chatLeadPrefill.test.ts.
 *
 * - Fonte: clients.lead_source contiene l'id di una riga di lead_sources (elenco
 *   configurabile dell'organizzazione). Si sceglie la fonte il cui nome contiene il
 *   canale; se l'organizzazione non ne ha una, il campo resta vuoto (nessuna fonte
 *   viene creata da qui).
 * - Dettaglio fonte: clients.lead_source_details, testo libero con il numero o il
 *   canale su cui e' arrivata la conversazione.
 */

export type ChatLeadChannel = 'whatsapp' | 'instagram' | 'messenger';

export interface LeadSourceLike { id: string; name: string | null }

/** Parole cercate nel nome della fonte, in ordine di preferenza. */
const SOURCE_KEYWORDS: Record<ChatLeadChannel, string[]> = {
  whatsapp: ['whatsapp'],
  instagram: ['instagram'],
  messenger: ['messenger', 'facebook'],
};

const CHANNEL_LABEL: Record<ChatLeadChannel, string> = {
  whatsapp: 'WhatsApp',
  instagram: 'Instagram',
  messenger: 'Messenger',
};

const compact = (s: string | null | undefined) => (s || '').toLowerCase().replace(/[\s_-]+/g, '');

/** Id della fonte adatta al canale, '' se l'organizzazione non ne ha una. */
export function pickChatLeadSourceId(sources: LeadSourceLike[], channel: ChatLeadChannel): string {
  for (const keyword of SOURCE_KEYWORDS[channel]) {
    const found = sources.find((s) => compact(s.name).includes(keyword));
    if (found) return found.id;
  }
  return '';
}

export interface ChatLeadInput {
  channel: ChatLeadChannel;
  name?: string | null;
  /** Solo cifre (es. 393331234567). */
  phone?: string | null;
  /** Numero aziendale su cui e' arrivata la chat WhatsApp (es. +39 02 1234567). */
  receivedOn?: string | null;
  /** Nome utente Instagram, senza @. */
  instagramUsername?: string | null;
  sources: LeadSourceLike[];
}

/** Testo per "Dettagli fonte": canale e, per WhatsApp, il numero che ha ricevuto la chat. */
export function chatLeadSourceDetails(channel: ChatLeadChannel, receivedOn?: string | null): string {
  const label = CHANNEL_LABEL[channel];
  const numero = (receivedOn || '').trim();
  return numero ? `Chat ${label} arrivata sul numero ${numero}` : `Chat ${label}`;
}

/** initialData per CreateClientModal / CreateClientDrawer (stessa shape di buildInitialFormData). */
export function buildChatLeadInitialData(input: ChatLeadInput): Record<string, string> {
  const data: Record<string, string> = {};
  const name = (input.name || '').trim();
  if (name) data.business_name = name;
  const phone = (input.phone || '').replace(/\D/g, '');
  if (phone.length >= 7) {
    data.phone = phone;
    data.whatsapp_number = phone;
  }
  const username = (input.instagramUsername || '').trim().replace(/^@/, '');
  if (input.channel === 'instagram' && username) data.instagram_url = `https://instagram.com/${username}`;
  const source = pickChatLeadSourceId(input.sources, input.channel);
  if (source) data.lead_source = source;
  data.lead_source_details = chatLeadSourceDetails(input.channel, input.receivedOn);
  return data;
}

/**
 * Lead/Cliente gia' presente con lo stesso numero, fra i risultati di una ricerca
 * (numbers = cifre con prefisso). Confronto sulle ultime 9 cifre, come il resto
 * delle chat, cosi' "+39 333..." e "333..." coincidono.
 */
export function findCandidateByNumber<T extends { numbers: string[] }>(candidates: T[], digits: string): T | null {
  const tail = (digits || '').replace(/\D/g, '').slice(-9);
  if (tail.length < 7) return null;
  return candidates.find((c) => c.numbers.some((n) => n.endsWith(tail))) ?? null;
}
